import { Button as AntdButton, Col, Form, message, Row, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../../redux/store";
import { UploadedDocument } from "../../../../../../types/document";
import { SelectOption } from "../../../../../../types/select";
import { InputField, ReactSelectField } from "../../../../../atoms/FormElement";
import { vehicleTypesOptions } from "../../../../../libs/constants";
import {
  getInputPlaceholderMessage,
  getRequiredErrorMessage,
  getSelectOptionValue,
  getSelectPlaceholderMessage,
  getValidationProps,
} from "../../../../../libs/form";
import Button from "../../../../../molecules/Button";
import { UploadDocument } from "../../../../../molecules/Documents";
import { getConfigData, getTenantDetails, patchTenantDetails } from "../../ducks/actions";
import { Vehicle, VehicleBase } from "./types";

export type VehicleForm = VehicleBase & {
  typeOfVehicle: SelectOption;
  city: SelectOption;
  document: UploadedDocument;
};

type VehicleDetailFormProps = {
  userId: string;
  onClose: () => void;
  defaultValues?: VehicleForm;
  vehicles?: Vehicle[];
};

export const VehicleDetailForm = (props: VehicleDetailFormProps) => {
  const { userId, onClose, defaultValues, vehicles } = props;

  const dispatch = useDispatch<AppDispatch>();
  const [emiratesOptions, setEmiratesOptions] = useState<SelectOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm<VehicleForm>({ defaultValues: defaultValues || {} });
  const { control, setValue, handleSubmit, reset, formState } = methods;
  const { errors } = formState;

  const defaultProps = { control, setValue };

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const onSubmit: SubmitHandler<Record<string, unknown>> = async (data: VehicleForm) => {
    const filtered: Vehicle[] = (vehicles ?? []).filter((vehicle) => vehicle.vehicleId !== data.vehicleId);

    const payload: Vehicle[] = [
      ...filtered,
      {
        vehicleId: data.vehicleId,
        typeOfVehicle: getSelectOptionValue(data.typeOfVehicle),
        brand: data.brand,
        model: data.model,
        modelYear: data.modelYear,
        color: data.color,
        plateNumber: data.plateNumber,
        plateCode: data.plateCode,
        city: getSelectOptionValue(data.city),
        documentId: data?.document?.documentId,
      },
    ];

    try {
      setIsSubmitting(true);
      await patchTenantDetails(userId, { vehicleInfo: payload });
      await dispatch(getTenantDetails(userId));

      onClose();
      reset();
    } catch (error) {
      message.error(error?.response?.data?.error ?? "Error occurred while updating tenant details");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    onClose();
    reset();
  };

  useEffect(() => {
    (async () => {
      try {
        const configData = await getConfigData("emiratesList");

        const emirates = configData.find((item) => "emiratesList" in item);

        const formatted = (emirates?.emiratesList ?? []).map((item: string) => ({ label: item, value: item }));
        setEmiratesOptions(formatted);
      } catch (error) {
        console.error("Failed to load emirates list:", error);
        message.error("Failed to load emirates list");
      }
    })();
  }, []);

  return (
    <FormProvider {...methods}>
      <Form
        layout="vertical"
        name="vehicleForm"
        scrollToFirstError
        className="bf-main"
        onFinish={handleSubmit(onSubmit)}
      >
        <Typography.Title level={5}>Vehicle Details</Typography.Title>
        <Row gutter={24}>
          <Col span={12}>
            <InputField
              fieldname="brand"
              label="Brand"
              {...defaultProps}
              {...getValidationProps({ fieldName: "brand", errors, isRequired: true })}
              iProps={{ placeholder: getInputPlaceholderMessage("brand", { formatAsTitleCase: true }) }}
            />
          </Col>
          <Col span={12}>
            <InputField
              fieldname="model"
              label="Model"
              {...defaultProps}
              {...getValidationProps({ fieldName: "model", errors, isRequired: true })}
              iProps={{ placeholder: getInputPlaceholderMessage("model", { formatAsTitleCase: true }) }}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <InputField
              fieldname="color"
              label="Color"
              {...defaultProps}
              {...getValidationProps({ fieldName: "color", errors, isRequired: true })}
              iProps={{ placeholder: getInputPlaceholderMessage("color", { formatAsTitleCase: true }) }}
            />
          </Col>
          <Col span={12}>
            <ReactSelectField
              fieldname="typeOfVehicle"
              label="Type"
              selectOption={vehicleTypesOptions}
              {...defaultProps}
              {...getValidationProps({ fieldName: "typeOfVehicle", errors, isRequired: true })}
              iProps={{ placeholder: getSelectPlaceholderMessage("typeOfVehicle") }}
            />
          </Col>
        </Row>

        <Typography.Title level={5}>License Plate</Typography.Title>
        <Row gutter={12}>
          <Col span={12}>
            <ReactSelectField
              fieldname="city"
              label="City"
              selectOption={emiratesOptions}
              {...defaultProps}
              {...getValidationProps({ fieldName: "city", errors, isRequired: true })}
              iProps={{ placeholder: getSelectPlaceholderMessage("city") }}
            />
          </Col>
          <Col span={12}>
            <InputField
              fieldname="plateCode"
              label="Plate code"
              {...defaultProps}
              {...getValidationProps({
                fieldName: "plateCode",
                errors,
                isRequired: true,
                rules: {
                  pattern: {
                    value: /^[A-Za-z]{1,2}$/,
                    message: "Please enter valid plate code, only 2 letters allowed",
                  },
                },
              })}
              iProps={{ placeholder: getInputPlaceholderMessage("plateCode", { formatAsTitleCase: true }) }}
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <InputField
              fieldname="plateNumber"
              label="Plate Number"
              {...defaultProps}
              {...getValidationProps({
                fieldName: "plateNumber",
                errors,
                isRequired: true,
                rules: {
                  pattern: {
                    value: /^[0-9]{1,6}$/,
                    message: "Please enter valid plate number, only numbers allowed (1-6 digits)",
                  },
                },
              })}
              iProps={{ placeholder: getInputPlaceholderMessage("plateNumber", { formatAsTitleCase: true }) }}
            />
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <UploadDocument
              isRequired
              fieldname="document"
              label="Registration document"
              control={control}
              maxSize={10}
              rules={{ required: { value: true, message: getRequiredErrorMessage() } }}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <AntdButton className="ef-discard-btn" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </AntdButton>
          <Button
            type="submit"
            label={isSubmitting ? "Submitting..." : "Submit"}
            className="ef-approve-btn"
            disabled={isSubmitting}
          />
        </Row>
      </Form>
    </FormProvider>
  );
};
